// First-screen review console for school admins. v1 uses a local
// seed; v2 can replace it with the live promotion-svc queue without
// changing component contracts.
import { useMemo, useState } from "react";
import { useAuth } from "@/auth/OidcProvider";
import { useTenant } from "@/tenants";
import { ActionRail } from "./components/ActionRail";
import { DocumentQueue } from "./components/DocumentQueue";
import { ProjectOverviewStrip } from "./components/ProjectOverviewStrip";
import { REVIEW_SEED, type ReviewItem } from "./components/reviewSeed";
import { SelectedReviewPanel } from "./components/SelectedReviewPanel";
import { WorkflowStats } from "./components/WorkflowStats";

function filterForRole(
  items: readonly ReviewItem[],
  isAdmin: boolean,
): readonly ReviewItem[] {
  return items.filter(
    (i) =>
      i.visibleTo === "all" ||
      (isAdmin ? i.visibleTo === "admin" : i.visibleTo === "non_admin"),
  );
}

export function DashboardPage(): JSX.Element {
  const { state } = useAuth();
  const tenant = useTenant();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const isAdmin =
    state.status === "authenticated" && state.user.isAdmin === true;

  const items = useMemo(() => filterForRole(REVIEW_SEED, isAdmin), [isAdmin]);
  const selected = items.find((i) => i.id === selectedId);

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-4">
      <ProjectOverviewStrip
        tenantName={tenant.displayName}
        activeSubjectCount={tenant.subjects.length}
      />
      <WorkflowStats
        items={items}
        activeSubjectCount={tenant.subjects.length}
      />
      <div className="grid gap-4 lg:grid-cols-[1.6fr_1fr]">
        <DocumentQueue
          items={items}
          selectedId={selectedId}
          onSelect={setSelectedId}
        />
        <SelectedReviewPanel item={selected} />
      </div>
      <ActionRail isAdmin={isAdmin} />
    </div>
  );
}

export default DashboardPage;
